/** /zh/comparisons/coda — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function CodaZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/comparisons/coda"
      documentTitle="面向 AI 原生产品团队的 Coda 替代方案：Draftlize vs Coda · Draftlize"
      documentDescription="给用 AI agent 跑产品工作的团队的 Coda 替代品。Coda 是「文档加数据库超能力」；Draftlize 是 AI 能读也能写的决策 substrate。给 PM 的对比。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">对比 · vs Coda</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Draftlize vs Coda：<br/>
            <em>文档 vs substrate。</em>
          </h1>
          <p className="lede">
            Coda 在它流行之前就把文档和数据库合在一起了。对结构化规划表、供应商矩阵、OKR 跟踪器——Coda 确实是同类最佳。但 Draftlize 答的问题——"我的 AI agent 跨对话把状态存哪"——不是 Coda 设计来回答的。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Coda 是工具。<em>Draftlize 是 substrate。</em></h2>
          </div>
          <p>Coda 让你建强大的文档。Draftlize 是 AI agent 拿来*建东西用*的。都正当，差别很大。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Coda</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>文档 + 表 + 数据库</td><td className="no">—</td><td className="yes">出色</td></tr>
            <tr><td>自定义 pack + 公式语言</td><td className="no">—</td><td className="yes">原生</td></tr>
            <tr><td>文档内置 AI 助手</td><td className="partial">agent 就是 substrate</td><td className="yes">Coda AI</td></tr>
            <tr><td>AI agent 跨文档读决策作为 context</td><td className="ours yes">原生</td><td className="partial">通过 @-mention</td></tr>
            <tr><td>AI agent 写结构化卡片回</td><td className="ours yes">原生</td><td className="partial">编辑 doc cell</td></tr>
            <tr><td>依赖追踪 + stale 传播</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>外部 AI 工具（Claude Code / Cursor）能读</td><td className="ours yes">MCP + CLI</td><td className="no">—</td></tr>
            <tr><td>团队工作空间 + 权限</td><td className="no">单人（v1）</td><td className="yes">出色</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Coda <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">给团队的结构化工具</h3>
            <p>OKR 跟踪、供应商打分、sprint dashboard、招聘流水线——把这些建成 Coda doc 你能得到一个真工具，不只是 wiki 页。Draftlize 不试图做数据库。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">如果你想*建*一个内部工具</h3>
            <p>Coda 的公式语言 + pack 让它是低代码平台。需要自定义计算器、带条件逻辑的工作流、连数据库的 airtable 替代？选 Coda。Draftlize 形状不对。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">给一起住在文档里的团队</h3>
            <p>Coda 的协作模型成熟。多用户实时、细粒度权限、评论。Draftlize 设计上是单人优先。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">你的"文档"是图，不是文档</h3>
            <p>产品思考里决策会依赖其他决策。文档把它表示成段落。图表示成<em>边</em>。改一个卡片，图知道什么依赖它。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">AI 在写一半的时候</h3>
            <p>Coda AI 适合"重写这段"。Draftlize agent 适合"我们 3 周前讨论过定价，这是我们决定了什么和为什么，让我更新依赖卡"。不同的 AI 协作模式。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 工具跨多个 app</h3>
            <p>Coda AI 住在 Coda 里。Draftlize 卡片通过 CLI 落 repo，通过 MCP 在 Claude Code / Cursor / Desktop 里浮现。你的决策从哪里都可寻址——不锁在一个画布。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Coda 让你建出色的文档。Draftlize 让你的 AI agent *用*出色的 substrate 建东西。
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            注册送 $5，按量付费，余额永不过期。Coda 管结构化工具。Draftlize 管 AI 辅助思考。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
